/**
 * Header Overlay Module
 * @description 헤더 겹치기(Overlay) 초기화
 *
 * 동작:
 * 1. 겹치기만: absolute처럼 동작 → 첫 번째 섹션과 오버랩
 * 2. 겹치기 + 고정: sticky 유지하면서 첫 번째 섹션을 헤더 높이만큼 위로 당김
 * 3. 첫 번째 섹션이 끝나는 시점에 배경 복원
 */

import { $ } from '../../../core/dom';

const HEADER_SELECTOR = '.nde-o-section-header';
const HEADER_WRAPPER_SELECTOR = '.nde-o-header';
const FOOTER_SELECTOR = '.nde-o-footer';
const POPUP_SELECTOR = '.nde-o-popup';
const UTILITY_MENU_ICON_SELECTOR = '.nde-c-utility-menu svg';
const SEARCH_ICON_SELECTOR = '.nde-c-header-search__icon-btn svg';

/**
 * 홈페이지(메인 페이지)인지 확인
 * - 서비스 환경: URL 경로로 판별 (TODO: 서버에서 판별값 제공 예정)
 * - 에디터 환경: 캔버스에서 주입하는 page-id 속성으로 판별
 */
const isHomePage = (): boolean => {
  // [서비스] 실제 운영 환경에서의 메인 페이지 체크
  const isServiceMainPage = window.__NDE_CURRENT_PAGE_NAME__ === 'main/index';

  // [에디터] 디자인 에디터 캔버스에서의 메인 페이지 체크
  const isEditorMainPage = document.querySelector('[nde-page-id="main-page"]') !== null;

  return isServiceMainPage || isEditorMainPage;
};

const SCROLLED_CLASS = 'nde-o-section-header--scrolled';
const BORDERED_CLASS = 'nde-o-section-header--bordered';

/**
 * 헤더 배경이 흰색(#FFFFFF)인지 판별
 * - setHeaderCssVariables에서 정규화한 CSS 변수를 기준으로 비교
 */
const isWhiteBackground = (header: HTMLElement): boolean => {
  const bgColor = header.style.getPropertyValue('--nde-header-background-color').trim().toLowerCase();
  return bgColor === '#ffffff' || bgColor === 'rgb(255, 255, 255)';
};

/**
 * 흰색 배경(#FFFFFF)이면 하단 경계선 클래스 추가
 */
const addBorderIfWhiteBg = (header: HTMLElement): void => {
  if (isWhiteBackground(header)) {
    header.classList.add(BORDERED_CLASS);
  }
};

/**
 * 헤더의 CSS 변수 설정 (배경색, 배경 이미지, 텍스트 색상)
 */
const setHeaderCssVariables = (header: HTMLElement): void => {
  const bgColor = header.style.backgroundColor;
  header.style.setProperty('--nde-header-background-color', bgColor === 'transparent' ? '#FFFFFF' : bgColor);
  header.style.setProperty('--nde-header-text-color', header.style.color);

  const bgImage = header.style.backgroundImage;
  if (bgImage) {
    header.style.setProperty('--nde-header-background-image', bgImage);
  }
};

/**
 * 유틸리티 메뉴 아이콘의 stroke 색상을 currentColor로 동기화
 */
const syncUtilityMenuIconColors = (header: HTMLElement): void => {
  const icons = header.querySelectorAll(`${UTILITY_MENU_ICON_SELECTOR}, ${SEARCH_ICON_SELECTOR}`);

  for (const icon of icons) {
    const strokeElements = icon.querySelectorAll('[stroke]');
    for (const el of strokeElements) {
      if (el.getAttribute('stroke') !== 'none') {
        (el as SVGElement).style.stroke = 'currentColor';
      }
    }
  }
};

/**
 * 고정 헤더의 마진 업데이트 (ResizeObserver 사용)
 */
const setupFixedHeaderMargin = (header: HTMLElement, firstSection: Element): void => {
  const updateSectionMargin = () => {
    const headerHeight = header.offsetHeight;
    (firstSection as HTMLElement).style.marginTop = `-${headerHeight}px`;
  };

  updateSectionMargin();

  const resizeObserver = new ResizeObserver(updateSectionMargin);
  resizeObserver.observe(header);
};

/**
 * 스크롤 감지로 헤더 상태 변경 (IntersectionObserver 사용)
 * - 첫 번째 섹션을 벗어나면 배경 복원 + 흰색 배경 시 경계선 추가
 */
const setupScrollObserver = (header: HTMLElement, firstSection: Element, showBorder: boolean): void => {
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const isScrolled = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        header.classList.toggle(SCROLLED_CLASS, isScrolled);
        header.classList.toggle(BORDERED_CLASS, isScrolled && showBorder);
      }
    },
    { threshold: 0 }
  );

  observer.observe(firstSection);
};

/**
 * 겹치기 모드에서 헤더와 페이지 첫 번째 섹션 간의 오버랩 설정
 * - 레이아웃 영역(header, footer, popup)을 배제하고 페이지 본문의 첫 번째 섹션을 기준으로 동작
 * - 고정 헤더: 해당 섹션에 음수 마진 적용 + 스크롤 시 배경 복원
 * - 비고정 헤더: CSS만으로 처리 (absolute positioning)
 */
const setupOverlayBehavior = (header: HTMLElement, showBorder: boolean): void => {
  const allSections = document.querySelectorAll('section');
  const firstPageSection = Array.from(allSections).find(
    (section) =>
      !section.closest(HEADER_WRAPPER_SELECTOR) && !section.closest(FOOTER_SELECTOR) && !section.closest(POPUP_SELECTOR)
  );

  if (!firstPageSection) {
    return;
  }

  const isFixed = header.classList.contains('nde-o-section-header--fixed');
  if (isFixed) {
    setupFixedHeaderMargin(header, firstPageSection);
    setupScrollObserver(header, firstPageSection, showBorder);
  }
};

/**
 * 헤더 오버레이 초기화
 *
 * 1. 모든 헤더: CSS 변수 및 아이콘 색상 설정
 * 2. 겹치기 모드: 홈페이지에서만 활성화, 그 외 페이지에서는 비활성화
 * 3. 겹치기 + 고정: 첫 번째 섹션과의 오버랩 동작 설정
 */
export const initHeaderOverlay = (): void => {
  const header = $(HEADER_SELECTOR) as HTMLElement | null;
  if (!header) {
    return;
  }

  // [1] 모든 헤더 공통: CSS 변수 및 아이콘 색상 설정
  setHeaderCssVariables(header);
  syncUtilityMenuIconColors(header);

  // [2] 겹치기 모드가 아니면 경계선 추가 후 종료
  const isOverlay = header.classList.contains('nde-o-section-header--transparent');
  if (!isOverlay) {
    addBorderIfWhiteBg(header);
    return;
  }

  // [3] 겹치기는 홈페이지에서만 작동 - 다른 페이지에서는 비활성화 후 경계선 추가
  if (!isHomePage()) {
    header.classList.remove('nde-o-section-header--transparent');
    addBorderIfWhiteBg(header);
    return;
  }

  // [4] 겹치기 + 고정 모드: 첫 번째 섹션과의 오버랩 설정
  setupOverlayBehavior(header, isWhiteBackground(header));
};
